import React from 'react';
import { LoginLayout, mobileAppAvailableCountries } from './LoginLayout';
import { LoginWidget } from './okta-widget-customizations/LoginWidget';
import { useSiteConfig } from '../config';
import { LoginLayoutZenith } from './LoginLayoutZenith';
import { ImThemeProviderZenith } from '@im/im-theme';
import { MuiProviders } from '@im/ui-lib/theme/MuiProviders';
import { useLocalizationContext, useTranslate } from '@im/react-localization';
import { IngramLogoWithRX4CLoginHeader } from '@im/ui-lib/icons/IngramLogoWithRX4CLoginHeader';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { IngramAppLinks } from './ingramAppLinks';
import { AppQrCode } from './appQrCode';
import { useTheme } from '@mui/material/styles';

type Props = {
  children?: JSX.Element;
  primaryAuthTitle?: string;
};

export const LoginView = ({ children, primaryAuthTitle }: Readonly<Props>) => {
  const translate = useTranslate();

  const { siteCode } = useSiteConfig();
  const zenithEnabled = mobileAppAvailableCountries.includes(
    siteCode?.toLowerCase()
  );

  return zenithEnabled ? (
    <ImThemeProviderZenith>
      <MuiProviders>
        <LoginLayoutZenith
          logo={<IngramLogoWithRX4CLoginHeader />}
          primaryAuthTitle={
            primaryAuthTitle
              ? primaryAuthTitle
              : translate('Login.WelcomeHeader')
          }
          showSplashBp="lg"
          splashContent={<SplashContent />}
          splashSx={{
            background: (theme) => theme.gradients['color-linear']['01'],
          }}
        >
          {children ? children : <LoginWidget />}
        </LoginLayoutZenith>
      </MuiProviders>
    </ImThemeProviderZenith>
  ) : (
    <LoginLayout>
      <LoginWidget />
    </LoginLayout>
  );
};

function SplashContent() {
  const translate = useTranslate();
  const mobilePreviewImgAlt = translate('Login.MobilePreviewAltText');

  const { locale } = useLocalizationContext();
  const theme = useTheme();

  return (
    <Stack
      alignItems="center"
      className="splash-content"
      direction="row"
      pl={9}
      pr={9}
      spacing={8}
    >
      <Stack maxWidth={485} minWidth={344} p={6} pl={7}>
        <Typography color="gray.950" variant="display-md-semibold">
          {translate('Login.SplashText')}
        </Typography>

        <Stack alignItems="center" direction="row" mb={8} mt={8} spacing={3.5}>
          <Stack maxWidth={142} spacing={2.5} width="100%">
            <IngramAppLinks width={'142px'} />
          </Stack>
          <AppQrCode
            size={130}
            style={{
              backgroundColor: theme.palette.common.white,
              border: `0.5px solid ${theme.palette.gray[200]}`,
              padding: '3px',
            }}
          />
        </Stack>

        <Divider sx={{ borderColor: 'gray.100', mb: 8, mt: 4 }} />

        <Typography color="gray.900" variant="text-sm-semibold">
          {translate('Login.SplashSupportingText')}
        </Typography>
        <Typography color="gray.600" variant="text-sm-regular">
          {translate('Login.SplashSubText')}
        </Typography>
      </Stack>

      <Stack
        flexShrink={1}
        sx={{ justifyContent: 'center', maxWidth: 240, width: '100%' }}
      >
        <img
          alt={mobilePreviewImgAlt}
          height="auto"
          src={`/cep/assets/img/login/mobile-preview/${locale}.webp`}
          width="100%"
        />
      </Stack>
    </Stack>
  );
}
